import { BAD_REQUEST } from "http-status-codes";
import humps from "humps";
import config from "../../config";
import { store } from "../../Store";
import actions from "../../Store/actions";
import { logToSentry } from "../../config/Sentry";
import { getMsalIdToken, getMsalAccessToken, acquireTokenSilently } from "./msalTokenHelper";
import { loginRequest } from "../../config/authConfig";

const { api } = config;
const { HTTP_FAILURE, HTTP_REQUEST, HTTP_SUCCESS } = actions.http.type;

const camelizeKeys = (data: any) =>
    humps.camelizeKeys(data, { split: config.regex.split });

const decamelizeKeys = (data: any) =>
    humps.decamelizeKeys(data, { split: config.regex.split });

/**
 * Check if the current MSAL token is expired
 * @returns {boolean} True if token is expired
 */
const isMsalTokenExpired = () => {
    try {
        const { getMsalInstance } = require("./msalTokenHelper");
        const msalInstance = getMsalInstance();

        if (!msalInstance) return false;

        const accounts = msalInstance.getAllAccounts();
        if (accounts.length === 0) return false;

        const account = accounts[0];
        if (!account.idTokenClaims || !account.idTokenClaims.exp) return false;

        const tokenExp = account.idTokenClaims.exp; // Unix timestamp in seconds
        const now = Math.floor(Date.now() / 1000); // Current time in seconds

        // Return true if token is expired
        return now >= tokenExp;
    } catch (error) {
        logToSentry("Error checking MSAL token expiration", {
            error: {
                name: error?.name,
                message: error?.message
            }
        });
        return false;
    }
};

const useJwt = (options: any = {}) => {
    const next = { ...options }; // preserve extra options like signal

    // Get MSAL token from MSAL's native storage (single source of truth)
    let idToken = getMsalIdToken() || getMsalAccessToken();

    if (!idToken) {
        return next;
    }

    next.headers = {
        ...(next.headers || {}),
        Authorization: `Bearer ${idToken}`
    };

    return next;
};

/**
 * Ensure a valid token is attached to the request options.
 * If the cached token is expired, attempt silent renewal via MSAL refresh token.
 * If renewal fails (refresh token also expired), redirect to login.
 */
const ensureFreshToken = async (options: any = {}) => {
    if (!isMsalTokenExpired()) {
        return options; // Token is still valid, nothing to do
    }

    // Token expired — attempt silent renewal using refresh token
    const freshToken = await acquireTokenSilently(loginRequest);

    if (freshToken) {
        // Renewal succeeded — update the Authorization header
        options.headers = {
            ...(options.headers || {}),
            Authorization: `Bearer ${freshToken}`
        };
        return options;
    }

    // Refresh token also expired — force re-login
    localStorage.setItem("session_expired", "true");
    window.location.href = `/${config.paths.login}`;
    throw new Error("Token expired");
};

const useContentTypeJson = (options: any = {}) => {
    const next = options;
    if (next.headers) {
        next.headers["Content-Type"] = "application/json";
    } else {
        next.headers = { "Content-Type": "application/json" };
    }
    return next;
};

const useContentTypeFormData = (options: any = {}) => {
    const next = options;
    if (next.headers) {
        next.headers["Content-Type"] = "multipart/form-data";
    } else {
        next.headers = { "Content-Type": "multipart/form-data" };
    }
    return next;
};

const anonymousJson = (options: any) => useContentTypeJson(options);
const anonymousForm = (options: any) => useContentTypeFormData(options);

const authorizedJson = (options: any) => useJwt(useContentTypeJson(options));
const authorizedForm = (options: any) =>
    useJwt(useContentTypeFormData(options));

// Special case: FormData POST where we should NOT set Content-Type manually
const authorizedPostFormData = (url: string, formData: FormData) =>
    httpRequest(
        url,
        useJwt({
            method: "POST",
            body: formData
        })
    );

/**
 * HTTP request with fetch API
 */
const httpRequest = async (url: string, options: any) => {
    // If request has an Authorization header, ensure the token is still valid
    if (options.headers?.Authorization) {
        await ensureFreshToken(options);
    }

    const request = { options, url };
    store.dispatch({
        type: HTTP_REQUEST,
        payload: { request }
    });
    try {
        const response = await fetch(
            url.startsWith("http") ? url : `${api.baseUrl}${url}`,
            options
        );
        const { status } = response;
        if (response.ok) {
            const text = await response.text();
            store.dispatch({
                type: HTTP_SUCCESS,
                payload: { response: text, status }
            });
            if (text) {
                const json = JSON.parse(text);
                const data = camelizeKeys(json);
                return Promise.resolve({ data, status });
            }
            return Promise.resolve({ status });
        }

        const error = await response.text();
        store.dispatch({
            type: HTTP_FAILURE,
            payload: { request, response, status },
            error
        });
        return Promise.reject(new Error(error));
    } catch (error) {
        const status = BAD_REQUEST;
        store.dispatch({
            type: HTTP_FAILURE,
            payload: { request, status },
            error
        });
        logToSentry("HTTP request error:", { error });
        return Promise.reject(new Error(error));
    }
};

export default {
    anonymous: {
        get: (url: string, headers: any = {}) =>
            httpRequest(
                url,
                anonymousJson({
                    headers,
                    method: "GET"
                })
            ),
        post: (url: string, data: any) =>
            httpRequest(
                url,
                anonymousJson({
                    method: "POST",
                    body: JSON.stringify(decamelizeKeys(data))
                })
            ),
        postForm: (url: string, form: any) =>
            httpRequest(
                url,
                anonymousForm({
                    method: "POST",
                    form
                })
            )
    },
    authorized: {
        get: (url: string) =>
            httpRequest(
                url,
                authorizedJson({
                    method: "GET"
                })
            ),
        patch: (url: string, data: any) =>
            httpRequest(
                url,
                authorizedJson({
                    method: "PATCH",
                    body: JSON.stringify(decamelizeKeys(data))
                })
            ),
        patchForm: (url: string, formData: FormData) =>
            httpRequest(
                url,
                authorizedForm({
                    method: "PATCH",
                    body: formData
                })
            ),
        post: (url: string, data: any) =>
            httpRequest(
                url,
                authorizedJson({
                    method: "POST",
                    body: JSON.stringify(decamelizeKeys(data))
                })
            ),

        postForm: (url: string, formData: FormData) =>
            httpRequest(
                url,
                useJwt({
                    method: "POST",
                    body: formData
                })
            )
    }
};
